import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useLocation } from 'react-router-dom';
import { useCustomer } from '../auth/CustomerContext';
import { useT } from '../i18n';
import { track } from '../tracking/tracker';
import { useConsent } from '../tracking/consent';
import { emitPageView } from './bus';
import { resolveLayers, resolvePreview, statRecorder } from './client';
import {
  MAX_POPUPS_PER_PAGE,
  MAX_POPUPS_PER_SESSION,
  importLegacyState,
  noteVisit,
  notePageView,
  passesFrequency,
  popupsShownThisSession,
  recordClosed,
  recordShown,
  recordSubmitted,
} from './store';
import type { LayerDelivery, LayerStatKind } from './types';

/* --------------------------------------------------------------- kontext */

interface LayerContextValue {
  /** Zvolený pop-up (jeden na page-view), už zaznamenaný jako zobrazený. */
  readonly popup: LayerDelivery | null;
  /** Weblayery vykreslované hostem: `announcement`, `floating_widget`. */
  readonly hostWeblayers: readonly LayerDelivery[];
  /** Kandidáti pro pojmenovaný slot (`banner`/`inline`/`recommendation`). */
  candidatesForSlot(name: string): readonly LayerDelivery[];
  /** Slot ohlásí, že vrstvu opravdu vykreslil (cap + imprese + track). */
  markSlotShown(layer: LayerDelivery): void;
  close(layer: LayerDelivery): void;
  interact(layer: LayerDelivery, kind: 'click' | 'submit'): void;
  /** Formulář vrstvy byl úspěšně odeslán (frekvenční cap: zhasnout natrvalo). */
  submitted(layer: LayerDelivery): void;
}

const LayerContext = createContext<LayerContextValue | null>(null);

/* --------------------------------------------------------------- helpery */

const CHECKOUT_PATHS = ['/kosik', '/pokladna', '/hotovo'];

function currentDevice(): 'desktop' | 'mobile' {
  return typeof window !== 'undefined' && window.innerWidth < 768 ? 'mobile' : 'desktop';
}

function isHostWeblayer(layer: LayerDelivery): boolean {
  return layer.kind === 'announcement' || layer.kind === 'floating_widget';
}

function isPopup(layer: LayerDelivery): boolean {
  return layer.surface === 'popup';
}

function isSlotLayer(layer: LayerDelivery): boolean {
  return layer.kind === 'banner' || layer.kind === 'inline' || layer.kind === 'recommendation';
}

function evergreenDurationOf(layer: LayerDelivery): number | undefined {
  for (const block of layer.doc.blocks) {
    if (block.type === 'countdown' && block.mode === 'evergreen') return block.durationSeconds;
  }
  return undefined;
}

function validNow(layer: LayerDelivery, now: number): boolean {
  if (layer.endsAt === null) return true;
  const end = new Date(layer.endsAt).getTime();
  return !Number.isFinite(end) || end > now;
}

function placementAllows(layer: LayerDelivery, path: string, device: string, lang: string): boolean {
  const p = layer.placement;
  const include = p.includePaths ?? [];
  if (include.length > 0 && !include.some((prefix) => path.startsWith(prefix))) return false;
  const exclude = p.excludePaths ?? [];
  if (exclude.some((prefix) => path.startsWith(prefix))) return false;
  if ((p.suppressOnCheckout ?? true) && CHECKOUT_PATHS.some((c) => path.startsWith(c))) return false;
  const devices = p.devices ?? [];
  if (devices.length > 0 && !devices.includes(device as 'desktop' | 'mobile')) return false;
  const languages = p.languages ?? [];
  if (languages.length > 0 && !languages.includes(lang)) return false;
  return true;
}

/* -------------------------------------------------------------- provider */

export function LayerProvider({ children }: { children: ReactNode }) {
  const location = useLocation();
  const { lang } = useT();
  const { customer, loading } = useCustomer();
  const { consent, bannerOpen } = useConsent();

  const [delivery, setDelivery] = useState<readonly LayerDelivery[]>([]);
  const [popup, setPopup] = useState<LayerDelivery | null>(null);
  const [hostWeblayers, setHostWeblayers] = useState<readonly LayerDelivery[]>([]);
  // Náhled z adminu („Vyzkoušet ve výloze") — server vypne cílení i triggery.
  // Vykreslí se hned, BEZ frekvenčního capu a BEZ zápisu statistiky.
  const [previewLayer, setPreviewLayer] = useState<LayerDelivery | null>(null);

  // Vrstvy, které si triggery „přejí" otevřít (naplní trigger engine).
  const requestedRef = useRef<Set<string>>(new Set());
  // Zavřené v této relaci stránky — nesmí se znovu otevřít.
  const dismissedRef = useRef<Set<string>>(new Set());
  // Pop-up se v této prohlídce stránky ukázal (globální strop 1/page-view).
  const popupThisPageRef = useRef(0);
  const [evalTick, forceEval] = useReducer((x: number) => x + 1, 0);

  const deviceRef = useRef(currentDevice());

  /* ---- statistika + track (jedno místo) ---- */

  const previewIdRef = useRef<string | null>(null);
  previewIdRef.current = previewLayer?.id ?? null;

  const recordStat = useCallback((layer: LayerDelivery, kind: LayerStatKind) => {
    // Náhled nikdy nezkresluje statistiku ani cap.
    if (layer.id === previewIdRef.current) return;
    statRecorder.record(layer.id, kind);
    // CDP event jen se souhlasem (track() sám dropne); NIKDY zdroj čísel v adminu.
    track(kind === 'impression' ? 'layer_shown' : 'layer_interacted', {
      layer_id: layer.id,
      surface: layer.surface,
      kind: layer.kind,
      interaction: kind,
    });
  }, []);

  const showLayer = useCallback(
    (layer: LayerDelivery) => {
      recordShown(layer, evergreenDurationOf(layer));
      recordStat(layer, 'impression');
    },
    [recordStat],
  );

  const interact = useCallback(
    (layer: LayerDelivery, kind: 'click' | 'submit') => {
      recordStat(layer, kind);
    },
    [recordStat],
  );

  const submitted = useCallback((layer: LayerDelivery) => {
    // Odeslání zháší vrstvu natrvalo (odběratel už nemá dostávat popup).
    recordSubmitted(layer.id);
  }, []);

  const close = useCallback(
    (layer: LayerDelivery) => {
      recordStat(layer, 'close');
      recordClosed(layer.id);
      dismissedRef.current.add(layer.id);
      setPopup((current) => (current?.id === layer.id ? null : current));
      setHostWeblayers((current) => current.filter((l) => l.id !== layer.id));
      setPreviewLayer((current) => (current?.id === layer.id ? null : current));
      forceEval();
    },
    [recordStat],
  );

  const markSlotShown = useCallback(
    (layer: LayerDelivery) => {
      if (layer.id === previewIdRef.current) return; // náhled se nezapočítává
      showLayer(layer);
    },
    [showLayer],
  );

  /* ---- doručení (resolve) ---- */

  const doResolve = useCallback(async () => {
    const response = await resolveLayers({
      path: location.pathname,
      lang,
      device: deviceRef.current,
    });
    importLegacyState(response.layers);
    setDelivery(response.layers);
    forceEval();
    return response.ttlSeconds;
  }, [location.pathname, lang]);

  // První načtení + re-resolve při změně identity / souhlasu. Čeká, až
  // doběhne první /me (loading), ať se cílení nespočítá pro „anonyma", který
  // je ve skutečnosti přihlášený.
  useEffect(() => {
    if (loading) return undefined;
    let alive = true;
    let ttlTimer: number | undefined;
    void doResolve().then((ttl) => {
      if (!alive) return;
      const seconds = Number.isFinite(ttl) && ttl > 0 ? ttl : 300;
      ttlTimer = window.setTimeout(() => {
        if (alive) void doResolve();
      }, seconds * 1000);
    });
    return () => {
      alive = false;
      if (ttlTimer !== undefined) window.clearTimeout(ttlTimer);
    };
    // Re-resolve při přihlášení/odhlášení a změně souhlasu (identita/consent
    // mění, co server pošle). Cesta se ZÁMĚRNĚ neúčastní — placement se řeší
    // na klientovi, takže odpověď na cestě nezávisí.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, customer?.id, consent.analytics, consent.marketing]);

  // Náhled: přečti token z URL jednou a vykresli danou vrstvu napřímo.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const layerId = params.get('layer_preview');
    const token = params.get('layer_token');
    if (layerId === null || token === null) return;
    void resolvePreview(
      { path: location.pathname, lang, device: deviceRef.current },
      { layerId, token },
    ).then((layers) => {
      const first = layers[0];
      if (first !== undefined) setPreviewLayer(first);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ---- návštěva + page-view + trigger engine ---- */

  const engineRef = useRef<import('./triggers').TriggerEngine | null>(null);
  // Nejnovější doručené vrstvy — engine se zakládá asynchronně (dynamický
  // import), takže by jinak mohl chytit prázdný seznam z mountu.
  const deliveryRef = useRef<readonly LayerDelivery[]>(delivery);

  useEffect(() => {
    noteVisit();
    // Trigger engine se založí jednou; onRequest naplní requestedRef.
    let engine: import('./triggers').TriggerEngine | null = null;
    void import('./triggers').then(({ createTriggerEngine }) => {
      engine = createTriggerEngine((layerId) => {
        requestedRef.current.add(layerId);
        forceEval();
      });
      engineRef.current = engine;
      engine.setLayers(deliveryRef.current);
    });
    return () => {
      engine?.stop();
      engineRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Předání aktuálních vrstev do enginu po každém resolve.
  useEffect(() => {
    deliveryRef.current = delivery;
    engineRef.current?.setLayers(delivery);
  }, [delivery]);

  // Page-view: reset stropu pop-upů na stránku, čítače, scroll v enginu.
  useEffect(() => {
    deviceRef.current = currentDevice();
    popupThisPageRef.current = 0;
    setPopup(null);
    notePageView();
    emitPageView(location.pathname);
    engineRef.current?.notePageView();
    forceEval();
  }, [location.pathname]);

  /* ---- vyhodnocení otevření (brána) ---- */

  const eligible = useCallback(
    (layer: LayerDelivery, now: number): boolean => {
      if (!requestedRef.current.has(layer.id)) return false;
      if (dismissedRef.current.has(layer.id)) return false;
      if (layer.surface === 'popup' && bannerOpen) return false;
      if (!validNow(layer, now)) return false;
      if (!placementAllows(layer, location.pathname, deviceRef.current, lang)) return false;
      if (!passesFrequency(layer, now)) return false;
      return true;
    },
    [bannerOpen, location.pathname, lang],
  );

  useEffect(() => {
    const now = Date.now();

    // 1) Pop-up: jeden na page-view i relaci, nikdy nad cookie lištou.
    if (
      popup === null &&
      !bannerOpen &&
      popupThisPageRef.current < MAX_POPUPS_PER_PAGE &&
      popupsShownThisSession() < MAX_POPUPS_PER_SESSION
    ) {
      const candidate = delivery.find((layer) => isPopup(layer) && eligible(layer, now));
      if (candidate !== undefined) {
        popupThisPageRef.current += 1;
        showLayer(candidate);
        setPopup(candidate);
      }
    }

    // 2) Host weblayery (announcement, floating_widget): 1 per slot (kind).
    const openKinds = new Set(hostWeblayers.map((l) => l.kind));
    const additions: LayerDelivery[] = [];
    for (const layer of delivery) {
      if (!isHostWeblayer(layer)) continue;
      if (openKinds.has(layer.kind)) continue;
      if (!eligible(layer, now)) continue;
      openKinds.add(layer.kind);
      additions.push(layer);
    }
    if (additions.length > 0) {
      for (const layer of additions) showLayer(layer);
      setHostWeblayers((current) => [...current, ...additions]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [evalTick, delivery, popup, hostWeblayers, bannerOpen, eligible]);

  /* ---- kandidáti pro sloty ---- */

  const candidatesForSlot = useCallback(
    (name: string): readonly LayerDelivery[] => {
      const now = Date.now();
      const normal = delivery.filter(
        (layer) => isSlotLayer(layer) && layer.slot === name && eligible(layer, now),
      );
      // Náhled slotové vrstvy má přednost (a obchází cap i targeting).
      if (previewLayer !== null && isSlotLayer(previewLayer) && previewLayer.slot === name) {
        return [previewLayer, ...normal];
      }
      return normal;
    },
    // evalTick drží funkci čerstvou vůči změnám requested/dismissed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [delivery, eligible, evalTick, previewLayer],
  );

  // Sloučení náhledu do vykreslovaných povrchů (host).
  const effectivePopup =
    previewLayer !== null && isPopup(previewLayer) ? previewLayer : popup;
  const effectiveHostWeblayers =
    previewLayer !== null && isHostWeblayer(previewLayer)
      ? [...hostWeblayers, previewLayer]
      : hostWeblayers;

  const value = useMemo<LayerContextValue>(
    () => ({
      popup: effectivePopup,
      hostWeblayers: effectiveHostWeblayers,
      candidatesForSlot,
      markSlotShown,
      close,
      interact,
      submitted,
    }),
    [effectivePopup, effectiveHostWeblayers, candidatesForSlot, markSlotShown, close, interact, submitted],
  );

  return <LayerContext.Provider value={value}>{children}</LayerContext.Provider>;
}

export function useLayers(): LayerContextValue {
  const context = useContext(LayerContext);
  if (context === null) throw new Error('useLayers musí být uvnitř <LayerProvider>.');
  return context;
}
